import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Check, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import logo from "@/assets/huelo-logo.svg";

type State = "validating" | "ready" | "submitting" | "done" | "already" | "invalid";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

const Unsubscribe = () => {
  const [state, setState] = useState<State>("validating");
  const token = new URLSearchParams(window.location.search).get("token");

  useEffect(() => {
    document.title = "Unsubscribe — Huelo";
    if (!token) {
      setState("invalid");
      return;
    }
    (async () => {
      try {
        const res = await fetch(
          `${SUPABASE_URL}/functions/v1/handle-email-unsubscribe?token=${encodeURIComponent(token)}`,
          { headers: { apikey: SUPABASE_ANON_KEY } },
        );
        const data = await res.json();
        if (data.valid) setState("ready");
        else if (data.reason === "already_unsubscribed") setState("already");
        else setState("invalid");
      } catch {
        setState("invalid");
      }
    })();
  }, [token]);

  const confirm = async () => {
    if (!token) return;
    setState("submitting");
    const { data, error } = await supabase.functions.invoke("handle-email-unsubscribe", {
      body: { token },
    });
    if (error) return setState("invalid");
    if (data?.success) setState("done");
    else if (data?.reason === "already_unsubscribed") setState("already");
    else setState("invalid");
  };

  return (
    <div className="min-h-[100dvh] bg-background flex flex-col items-center justify-center px-6 py-10">
      <div className="w-full max-w-md text-center">
        <div className="flex flex-col items-center mb-8">
          <img src={logo} alt="Huelo" className="h-10 w-auto mb-3" />
        </div>
        <div className="rounded-2xl border border-border bg-card p-8 shadow-sm">
          {state === "validating" && (
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Checking your link…</p>
            </div>
          )}
          {state === "ready" && (
            <>
              <h1 className="font-display text-xl text-foreground mb-2">Unsubscribe from Huelo emails</h1>
              <p className="text-sm text-muted-foreground mb-6">
                Confirm below and we'll stop sending you Huelo emails.
              </p>
              <Button onClick={confirm} className="w-full rounded-full">
                Confirm unsubscribe
              </Button>
            </>
          )}
          {state === "submitting" && (
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Unsubscribing…</p>
            </div>
          )}
          {(state === "done" || state === "already") && (
            <div className="space-y-3">
              <div className="mx-auto h-12 w-12 rounded-full bg-muted flex items-center justify-center">
                <Check className="h-6 w-6 text-foreground" />
              </div>
              <h1 className="font-display text-xl text-foreground">You're unsubscribed</h1>
              <p className="text-sm text-muted-foreground">
                {state === "already"
                  ? "This email is already off our list."
                  : "We won't send you Huelo emails anymore."}
              </p>
            </div>
          )}
          {state === "invalid" && (
            <div className="space-y-3">
              <div className="mx-auto h-12 w-12 rounded-full bg-muted flex items-center justify-center">
                <AlertCircle className="h-6 w-6 text-foreground" />
              </div>
              <h1 className="font-display text-xl text-foreground">Link not valid</h1>
              <p className="text-sm text-muted-foreground">
                This unsubscribe link is invalid or expired.
              </p>
            </div>
          )}
        </div>
        <p className="text-center text-sm text-muted-foreground mt-6">
          <Link to="/" className="text-foreground underline underline-offset-4 hover:opacity-80">
            Back to Huelo
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Unsubscribe;
